import select from 'dom-select';
import isOutOfViewport from './isOutOfViewport';

/**
 * Global tooltip state manager
 * Coordinates behavior across all tooltip instances
 */

const POSITION_MAP = {
  right: 'flush-right',
  left: 'flush-left',
  top: 'flush-top',
  bottom: 'flush-bottom',
};

/**
 * Removes all position classes from a tooltip content element
 * @param {HTMLElement} tooltipContent - The tooltip content element
 */
export const clearTooltipPosition = (tooltipContent) => {
  if (!tooltipContent) return;
  tooltipContent.classList.remove(...Object.values(POSITION_MAP));
};

/**
 * Applies viewport-aware positioning classes to a tooltip content element
 * @param {HTMLElement} tooltipContent - The tooltip content element
 */
export const applyTooltipPosition = (tooltipContent) => {
  if (!tooltipContent) return;

  // Clear existing position classes
  clearTooltipPosition(tooltipContent);

  // Determine viewport overflow
  const vpCheck = isOutOfViewport(tooltipContent);

  // Apply position classes based on viewport check
  Object.entries(POSITION_MAP).forEach(([direction, className]) => {
    if (vpCheck[direction]) {
      tooltipContent.classList.add(className);
    }
  });
};

/**
 * Closes all tooltips and clears their positioning classes
 */
const closeAllTooltips = () => {
  const allTooltips = select.all('.details--tooltip', document);

  allTooltips.forEach((tooltip) => {
    tooltip.removeAttribute('open');
  });

  const allTooltipContents = select.all('.details--tooltip .details__content', document);
  allTooltipContents.forEach((content) => {
    clearTooltipPosition(content);
  });
};

/**
 * Manages tooltip open/close state based on user interactions
 * Handles clicks inside/outside tooltips and closes tooltips on resize
 * @param {Event} event - The triggering event (click or resize)
 */
export const manageTooltipState = (event) => {
  // On resize, just close all tooltips
  if (event.type === 'resize') {
    closeAllTooltips();
    return;
  }

  // Handle click events
  const target = event.target;
  const allTooltips = select.all('.details--tooltip', document);
  const clickedTooltip = allTooltips.find((tooltip) => tooltip.contains(target));

  // If click is on a summary element, let native toggle handle it
  // but close all OTHER tooltips
  if (clickedTooltip) {
    const clickedSummary = clickedTooltip.querySelector('.summary');

    if (clickedSummary && clickedSummary.contains(target)) {
      // Close all tooltips except the one being toggled
      allTooltips.forEach((tooltip) => {
        if (tooltip !== clickedTooltip) {
          tooltip.removeAttribute('open');
        }
      });
      return;
    }

    // If clicking inside tooltip content (not summary), close it and focus summary
    const clickedContent = clickedTooltip.querySelector('.details__content');
    if (clickedContent && clickedContent.contains(target)) {
      clickedTooltip.removeAttribute('open');
      const summary = clickedTooltip.querySelector('.summary');
      // Only refocus on actual mouse clicks, not keyboard activation
      if (summary && event.pointerType !== undefined) {
        summary.focus();
      }
      return;
    }
  }

  // Click was outside all tooltips - close everything
  closeAllTooltips();
};

export default manageTooltipState;
